import React from 'react';

import { Select } from '@mantine/core';
import { ArrowUpDown } from 'lucide-react';

// ============================================================
// Styles — every className lives here, zero inline Tailwind in JSX
// ============================================================

const s = {
  col: 'flex flex-col gap-1',
  sortRow: 'flex items-center gap-1 leading-none',
  sortRowClickable: 'flex items-center gap-1 leading-none cursor-pointer select-none',
  label: 'whitespace-nowrap',
  sortIcon: 'opacity-40 translate-y-px',
  sortIconSvg: 'w-3 h-3',
} as const;

const filterSelectStyles = {
  input: { height: 22, minHeight: 22, fontSize: 11, padding: '0 6px' },
  dropdown: { maxHeight: 200 },
} as const;

interface FilterableHeaderColumn {
  getToggleSortingHandler: () => ((event: unknown) => void) | undefined;
  getCanSort: () => boolean;
  getFilterValue: () => unknown;
  setFilterValue: (value: unknown) => void;
}

interface FilterableHeaderProps {
  title: string;
  column: FilterableHeaderColumn;
  /** Whether to render the filter dropdown (default: true) */
  showFilter?: boolean;
  /** All possible values for the dropdown options */
  allValues?: string[];
}

/**
 * Column header for the outer clonotype table.
 * Renders a sort toggle (ArrowUpDown icon) and an optional
 * Mantine Select dropdown for column-level value filtering.
 */
export function FilterableHeader({ title, column, showFilter = true, allValues = [] }: FilterableHeaderProps) {
  const options = React.useMemo(() => {
    const unique = Array.from(new Set(allValues.filter((v) => v && v !== '-')));
    return unique.sort();
  }, [allValues]);

  const filterValue = (column.getFilterValue() as string) ?? '';

  return (
    <div className={s.col}>
      <div className={column.getCanSort() ? s.sortRowClickable : s.sortRow} onClick={column.getToggleSortingHandler()}>
        <span className={s.label}>{title}</span>
        {column.getCanSort() && (
          <span className={s.sortIcon}>
            <ArrowUpDown className={s.sortIconSvg} />
          </span>
        )}
      </div>
      {showFilter && (
        <Select
          placeholder="Filter by"
          data={options}
          value={filterValue || null}
          onChange={(val) => column.setFilterValue(val || undefined)}
          size="xs"
          clearable
          searchable
          styles={filterSelectStyles}
          onClick={(e) => e.stopPropagation()}
          comboboxProps={{ withinPortal: true }}
        />
      )}
    </div>
  );
}
